import { FunctionComponent, useCallback, useEffect, useState } from 'react'

import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import useStyles from './choice-style'
import { ClinicAndFlags } from '@/modules/Auth/auth-types'
import UserProfileCard from '@/modules/Auth/components/UserProfileCard/index'
import { Link, toast } from '@conexasaude/hero'
import Icon from '@/components/HeroIcons'
import { useHistory, useParams, useLocation } from 'react-router'
import { Location } from 'history'
import service from '../../auth-services'
import themeServices from '@/theme/theme.services'
import { useMedicalSpecialitiesContext } from '@/modules/MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { User } from '@/modules/Auth/classes/User'
import LoadingPage from '@/components/LoadingPage'
import redirectToOldApplication from '@/utils/redirectToOldApplication'
import { setRefreshToken, setToken } from '@/utils/token'
import CustomLayout from '@/components/CustomLayout'
// import { getUserAgent } from '@/utils/dom'
// import { NewAccessModal } from '../../components/NewAccessModal/index'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import { TermsAndConditionsModal } from '../../components/TermsAndConditionsModal'
import { SpecialtiesModal } from '../../components/Modal/SpecialtiesModal'
import { useChatLayerStore } from '@/components/ChatLayer'
import { setCache } from '@/utils/cache'
import { RenderIf } from '@/components/RenderIf'

type ChoiceLocation = {
  from: Location
}

const Choice: FunctionComponent = () => {
  const params = useParams<{ clinic?: string }>()
  const location = useLocation<ChoiceLocation>()
  const history = useHistory()
  const { container, pageContainer } = useStyles()
  const { users, choiceUser, setUserClinic } = useAuthContext()
  const { setThemeColors } = useThemeGeneratorContext()
  const { setSpecialities } = useMedicalSpecialitiesContext()
  const { showModal } = useModalContext()
  const openChatLayer = useChatLayerStore(state => state.openChatLayer)

  const [loading, setLoading] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)
  const [currentProfileId, setCurrentProfileId] = useState(0)
  const [termsModalActive, setTermsModalActive] = useState(false)
  const [imageError, setImageError] = useState(false)

  const redirectAfterChoice = useCallback(
    (pathname?: string) => {
      if (location?.state?.from) {
        history.push(location?.state?.from?.pathname)
        return
      }

      history.push(pathname)
    },
    [history, location?.state?.from],
  )

  const setIfHaveOneUser = useCallback(async () => {
    try {
      setCurrentUser(users[0])
      const token = users[0].token
      const refreshToken = users[0].refreshToken
      setToken(token)
      setRefreshToken(refreshToken)
      setCache('user', users[0].id)

      const termsChecked = await service.checkTermsAcceptance()
      setLoading(true)
      const userClinic: ClinicAndFlags = await service.getClinic(users[0].token)
      const clinicColors = await themeServices.getClinicColorsById(
        userClinic.clinic.idClinica,
      )
      const ip = await service.getUserIP().catch(() => {
        console.error('Erro ao pegar ip')
        return ''
      })
      if (!users[0]?.flagAccessedBefore)
        await service.validateFirstAccess(users[0]?.token)
      choiceUser(users[0].id, ip)
      if (!termsChecked) {
        setTermsModalActive(true)
        showModal('TERMS_AND_CONDITIONS', {
          title: 'Termos e Condições Gerais de Uso',
          persistent: true,
        })
        return
      }
      setThemeColors(clinicColors)
      setUserClinic(userClinic)
      setSpecialities(userClinic.clinic)
      redirectAfterChoice('/')
    } catch (e) {
      toast('Algo de errado ocorreu. Tente novamente', {
        variant: 'danger',
      })
    }
  }, [
    choiceUser,
    setSpecialities,
    setThemeColors,
    setUserClinic,
    users,
    showModal,
    redirectAfterChoice,
  ])

  const setUser = useCallback(
    async (user: User) => {
      setTermsModalActive(false)
      try {
        setLoading(true)
        setCurrentProfileId(user.id)
        const userClinic: ClinicAndFlags = await service.getClinic(user.token)
        const clinicColors = await themeServices.getClinicColorsById(
          userClinic.clinic.idClinica,
        )
        const ip = await service.getUserIP().catch(() => {
          console.error('Erro ao pegar ip')
          return ''
        })
        if (!user?.flagAccessedBefore)
          await service.validateFirstAccess(user?.token)
        choiceUser(user.id, ip)
        setThemeColors(clinicColors)
        setUserClinic(userClinic)
        setSpecialities(userClinic.clinic)
        redirectAfterChoice('/')
      } catch (e) {
        console.log(e)
        setLoading(false)
        setCurrentProfileId(0)
        toast('Algo de errado ocorreu. Tente novamente', {
          variant: 'danger',
        })
      }
    },
    [
      choiceUser,
      setSpecialities,
      setThemeColors,
      setUserClinic,
      redirectAfterChoice,
    ],
  )

  const handleUserSelect = useCallback(async (user: User) => {
    try {
      setLoading(true)
      setCurrentProfileId(user.id)
      setCurrentUser(user)
      await service.validateToken(user.token)

      setToken(user.token)
      setRefreshToken(user.refreshToken)
      const termsChecked = await service.checkTermsAcceptance()
      if (!termsChecked) {
        setTermsModalActive(true)
        showModal('TERMS_AND_CONDITIONS', {
          title: 'Termos e Condições Gerais de Uso',
          persistent: true,
        })
        setLoading(false)
        setCurrentProfileId(0)
        return
      }
      if (!user.isAllowedToNewApplication) {
        setLoading(true)
        setCurrentProfileId(user.id)
        setToken(user.token)
        redirectToOldApplication()
        setLoading(false)
        setCurrentProfileId(0)
        return
      }
      setUser(user)
    } catch (err) {
      setLoading(false)
      setCurrentProfileId(0)
      const errorMessage =
        err?.response?.data?.msg ||
        err?.response?.data?.object?.msg ||
        err.message
      toast(errorMessage, {
        variant: 'danger',
      })
    }
    setUser(user)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!users) {
      history.push('/login', {
        from: location?.state?.from,
      })
    } else if (users?.length === 1) {
      if (!users[0]?.isAllowedToNewApplication) {
        redirectToOldApplication(users[0]?.token)
        return
      }

      setIfHaveOneUser()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onShowModalSpeciality = useCallback(
    (user: User) => {
      setCurrentUser(user)
      setTimeout(() => {
        showModal('SPECIALTIES_MODAL', {
          title: 'Especialidades',
        })
      }, 1)
    },
    [showModal],
  )

  return (
    <CustomLayout>
      <div css={pageContainer}>
        <div className="header_choice">
          <div>
            <RenderIf condition={!!params.clinic && !imageError}>
              <img
                src={`${process.env.REACT_APP_LINK_LOGO_AWS}${params.clinic}.png`}
                alt="logo"
                className={'logo-whitelabel mb-3'}
                onError={event => {
                  event.currentTarget.onerror = null
                  setImageError(true)
                  event.currentTarget.style.height = 'auto'
                }}
              />
            </RenderIf>
            <RenderIf condition={!params.clinic || imageError}>
              <Icon
                name="conexa-logo"
                width={'125px'}
                className="icon_header_choice"
              />
            </RenderIf>
          </div>
        </div>
        <div css={container}>
          {users?.length > 1 && (
            <div className="titulos_layout">
              <div>
                <h1 className="h1_choice">O que deseja acessar?</h1>
                <h3 className="h3_choice">
                  Escolha a clínica, hospital ou convênio.
                </h3>
              </div>
            </div>
          )}
          {users?.length > 1 ? (
            users.map(user => (
              <UserProfileCard
                className="MultiPerfil-SeleçãoPerfil-Clique_perfil"
                photo={user.photo}
                name={user.socialNameOrName}
                busy={loading && currentProfileId === user.id}
                clinic={user.clinic.name}
                onClick={() => handleUserSelect(user)}
                onActionClick={() => onShowModalSpeciality(user)}
                key={user.id}
                type={user.holder}
                token={user.token}
              />
            ))
          ) : (
            <div className="mt-3">
              {!termsModalActive && <LoadingPage withoutNav={true} />}
            </div>
          )}
          <Link className="link-support-choice" onClick={openChatLayer}>
            Precisa de ajuda?
          </Link>
        </div>
        <TermsAndConditionsModal
          redirectTo={''}
          directLogin={() => setUser(currentUser)}
          setLoading={setLoading}
          userToken={currentUser?.token}
        />
        <SpecialtiesModal
          photo={currentUser?.photo}
          name={currentUser?.socialNameOrName}
          clinic={currentUser?.clinic.name}
          key={currentUser?.id}
          type={currentUser?.holder}
          token={currentUser?.token}
        />
      </div>
    </CustomLayout>
  )
}

export default Choice
